import { usePrevious } from "@chakra-ui/react";
import { isEqual } from "lodash-es";
import loglevel from "loglevel";
import React, {
  Children,
  ElementType,
  isValidElement,
  NamedExoticComponent,
  ReactNode,
  useRef,
} from "react";
import { isDev } from "shared/utils/DevUtils";
import { mergeProps } from "shared/utils/MergeProps";

const log = loglevel.getLogger("RenderLoop");
log.setDefaultLevel(isDev ? "trace" : "silent");

export const genericMemo: <T>(component: T) => T & NamedExoticComponent =
  React.memo;

const hasChildren = (
  element: React.ReactNode,
): element is React.ReactElement<React.PropsWithChildren> =>
  React.isValidElement<React.PropsWithChildren>(element) &&
  Boolean(element.props.children);

export const isElementOfType = <T extends ElementType>(
  element: React.ReactNode,
  type: T,
): element is React.ReactElement<React.ComponentProps<T>> => {
  if (!React.isValidElement(element)) {
    return false;
  }
  if (element.type === type) {
    return true;
  }
  if (isDev) {
    // Comparing type instances does not work reliably during development
    // due to hot module replacement, so fall back to comparing type names.
    const elementTypeName = (element.type as React.FC | undefined)?.name;
    const typeName = (type as React.FC | undefined)?.name;
    return Boolean(elementTypeName && typeName && elementTypeName === typeName);
  }
  return false;
};

export const hasChildOfType = <T extends ElementType>(
  children: ReactNode | ReactNode[],
  type: T,
): boolean =>
  React.Children.toArray(children).some((child) =>
    isElementOfType(child, type),
  );

export const firstChildOfType = <T extends ElementType>(
  children: ReactNode | ReactNode[],
  type: T,
  fn: (child: React.ReactElement<React.ComponentProps<T>>) => boolean = () =>
    true,
): React.ReactElement<React.ComponentProps<T>> | undefined => {
  for (const child of React.Children.toArray(children)) {
    if (isElementOfType(child, type) && fn(child)) {
      return child;
    } else if (hasChildren(child)) {
      const result = firstChildOfType(child.props.children, type);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
};

export const deepFindElementChild = <T extends ElementType>(
  children: React.ReactNode | React.ReactNode[],
  fn: (
    child: React.ReactNode,
  ) => child is React.ReactElement<React.ComponentProps<T>>,
): React.ReactElement<React.ComponentProps<T>> | undefined => {
  for (const child of React.Children.toArray(children)) {
    if (fn(child)) {
      return child;
    } else if (hasChildren(child)) {
      const result = deepFindElementChild(child.props.children, fn);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
};

/**
 * Gets only the valid children of a component,
 * and ignores any nullish or falsy child.
 *
 * From: https://github.com/chakra-ui/chakra-ui/blob/main/packages/utilities/react-children-utils/src/index.ts
 *
 * @param children the children
 */
export const getValidChildren = (children: React.ReactNode) =>
  Children.toArray(children).filter((child) =>
    isValidElement(child),
  ) as React.ReactElement[];

export const getChildAsString = (children: React.ReactNode): string =>
  Children.toArray(children)
    .filter((child) => typeof child === "string")
    .join("");

export const useDetectRenderLoop = () => {
  const renderCount = useRef(0);
  const lastRenderAt = useRef(Date.now());
  if (isDev) {
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderAt.current;
    if (timeSinceLastRender > 1_000) {
      renderCount.current = 0;
    }
    lastRenderAt.current = now;
    renderCount.current += 1;
    if (renderCount.current > 100) {
      log.warn("possible render loop detected");
    }
  }
};

export const useMergedProps = <T extends object>(...newArgs: T[]): T => {
  const prevArgs = usePrevious(newArgs);
  const stableProps = useRef<T>({} as T);
  if (!isEqual(prevArgs, newArgs)) {
    stableProps.current = mergeProps(...newArgs) as T;
  }
  return stableProps.current;
};

export const isValidElementWithChildren = (
  maybeElement: unknown,
): maybeElement is React.ReactElement<React.PropsWithChildren> =>
  React.isValidElement<React.PropsWithChildren>(maybeElement) &&
  Boolean(maybeElement.props.children);
